import { useTranslation } from 'react-i18next';
import { Flex, Box } from 'reflexbox';
import _ from 'lodash';

import { Heading, Select } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import GraphQLErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/GraphQLErrorMessage';
import { useWorkspace } from 'packages/kakapo-ui-services/hooks/useWorkspace';
import useErrorBoundary from 'packages/kakapo-ui-application/hooks/useErrrorBoundary';
import useAuthenticatedUser from 'packages/kakapo-ui-application/hooks/useAuthenticatedUser';

function WorkspaceFeature(props: { feature: graphql.WorkspaceFeature }) {
	const { t } = useTranslation();
	const viewer = useAuthenticatedUser();

	const [update, { loading }] = graphql.useUpdateWorkspaceFeatureMutation();

	const [error, attempt] = useErrorBoundary();

	const updateWorkspaceFeature = attempt(
		async (featureID: string, status: graphql.WorkspaceFeatureStatus) => {
			await update({ variables: { input: { featureID, status } } });
		},
	);

	const statuses = [
		graphql.WorkspaceFeatureStatus.Enabled,
		graphql.WorkspaceFeatureStatus.Superuser,
		graphql.WorkspaceFeatureStatus.Disabled,
	];

	if (viewer.username === 'support') {
		statuses.push(graphql.WorkspaceFeatureStatus.Restricted);
	}

	const options = statuses.map((status) => ({
		value: status,
		title: t(`settings.organization.featureStatus.${status.toLowerCase()}`),
	}));

	return (
		<Box py={2}>
			<GraphQLErrorMessage error={error} />
			<Flex flexDirection="row" alignItems="center">
				<Box flexGrow={1} flexShrink={1}>
					{t(`settings.organization.settings.${props.feature.feature.id.replace(/\./g, '_')}`)}
				</Box>
				<Box width={200} ml={2} flexGrow={0} flexShrink={0}>
					{loading ? (
						<Select.Skeleton />
					) : (
						<Select
							search={false}
							selectedValue={props.feature.status}
							options={options}
							onChange={(option) => {
								updateWorkspaceFeature(
									props.feature.feature.id,
									option! as graphql.WorkspaceFeatureStatus,
								);
							}}
						/>
					)}
				</Box>
			</Flex>
		</Box>
	);
}

export default function WorkspaceFeatures() {
	const { t } = useTranslation();

	const workspace = useWorkspace();
	const viewer = useAuthenticatedUser();

	const { data } = graphql.useWorkspaceFeaturesQuery({
		variables: {
			id: workspace.id,
		},
		fetchPolicy: 'cache-first',
	});

	const order = _.fromPairs(
		['general', 'cycles', 'goals', 'feedback', 'conversations', 'learning', 'notes'].map(
			(name, i) => [name, i],
		),
	);

	const map = _.fromPairs(
		_.entries({
			goals: ['2021.goals', '2021.teamGoals', '2024.developmentPlan'],
			notes: ['2021.notes'],
			feedback: [
				'2021.feedback',
				'2021.compliments',
				'2021.feedback.adhoc',
				'2021.feedback.goals',
				'2021.feedback.competencies',
				'2023.feedback.spontaneous',
			],
			conversations: [
				'2021.evaluations',
				'2021.evaluations.adhoc',
				'2021.evaluations.override',
				'2021.evaluations.context',
				'2024.evaluations.defaultWrite',
			],
			skills: ['2021.jobCompetencies', '2023.jobCompetencies.explorer'],
			learning: ['2021.learning'],
			cycles: ['2021.cycles'],
		}).flatMap(([key, values]) => values.map((value) => [value, key])),
	);

	const groups: Record<string, graphql.WorkspaceFeature[]> = {};

	for (const feature of data?.workspace.features.filter(
		(feature) => feature.feature.stability !== graphql.FeatureStability.Alpha,
	) ?? []) {
		const group = map[feature.feature.id] ?? 'general';

		groups[group] ??= [];
		groups[group].push(feature);
	}

	return (
		<>
			{_.sortBy(_.entries(groups), ([name]) => order[name]).map(([name, features], i) => (
				<Box mb={4} key={name}>
					<Heading mb={2}>{t(`settings.features.${name}.title`)}</Heading>
					{features.map((feature) => (
						<WorkspaceFeature key={feature.id} feature={feature} />
					))}
				</Box>
			))}

			{viewer.username === 'support' && (
				<Box mb={4}>
					<Heading mb={2}>{t('settings.features.experimental.title')}</Heading>
					{data?.workspace.features
						.filter((feature) => feature.feature.stability === graphql.FeatureStability.Alpha)
						.map((feature) => <WorkspaceFeature key={feature.id} feature={feature} />)}
				</Box>
			)}
		</>
	);
}
